/**
 * Place search over the bundled town list (GeoNames cities with 15,000+ people, CC BY 4.0), built by
 * `scripts/build-places.ts` into `web/public/places/<key>.json`: one small file per two-letter prefix
 * of a name's words, so a search downloads a few kilobytes.
 */

/** [name, region (state/province), ISO country code, lat, lng, population, other names ("|"-joined)] */
export type PlaceEntry = [string, string, string, number, number, number, string?];

/** Letters without a plain base letter in Unicode (accents come off by themselves). */
const LETTERS: Record<string, string> = {
  ł: 'l',
  ø: 'o',
  đ: 'd',
  ð: 'd',
  ß: 'ss',
  æ: 'ae',
  œ: 'oe',
  þ: 'th',
  ı: 'i',
};

/** Lower case, no accents, words separated by single spaces: what searches compare. */
export function normalizePlaceName(s: string): string {
  return s
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[łøđðßæœþı]/g, (c) => LETTERS[c]!)
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** The data file holding every place with a word starting like `query` (null: too short). */
export function placeFileKey(query: string): string | null {
  const word = normalizePlaceName(query.split(',')[0] ?? '').split(' ')[0] ?? '';
  return word.length >= 2 ? word.slice(0, 2) : null;
}

/** The data files a place belongs in: one per two-letter start of each word of its names. */
export function placeFileKeys(entry: PlaceEntry): string[] {
  const words = placeNames(entry).flatMap((n) => n.split(' '));
  return [...new Set(words.filter((w) => w.length >= 2).map((w) => w.slice(0, 2)))];
}

function placeNames(entry: PlaceEntry): string[] {
  return [entry[0], ...(entry[6] ? entry[6].split('|') : [])].map(normalizePlaceName);
}

/**
 * Places matching `query`, best first: exact names, then names with a word starting with it, bigger
 * places first within each. Text after a comma narrows by region or country ("Springfield,
 * Illinois", "Kochi, Japan", "Paris, US").
 */
export function searchPlaces(
  entries: readonly PlaceEntry[],
  query: string,
  countryName: (code: string) => string,
  limit = 8,
): PlaceEntry[] {
  const [namePart = '', ...rest] = query.split(',');
  const q = normalizePlaceName(namePart);
  const where = normalizePlaceName(rest.join(' '));
  if (q.length < 2) return [];
  const ranked: Array<{ entry: PlaceEntry; rank: number }> = [];
  for (const entry of entries) {
    let rank = Infinity;
    for (const name of placeNames(entry)) {
      if (name === q) rank = 0;
      else if (name.startsWith(q) || name.includes(` ${q}`)) rank = Math.min(rank, 1);
    }
    if (rank === Infinity) continue;
    if (where) {
      const [, region, cc] = entry;
      const inPlace =
        normalizePlaceName(region).startsWith(where) ||
        normalizePlaceName(countryName(cc)).startsWith(where) ||
        cc.toLowerCase() === where;
      if (!inPlace) continue;
    }
    ranked.push({ entry, rank });
  }
  return ranked
    .sort((a, b) => a.rank - b.rank || b.entry[5] - a.entry[5])
    .slice(0, limit)
    .map((r) => r.entry);
}

/**
 * A town's address on the site, e.g. `kochi-in` for /town/kochi-in: its name and country code, so
 * same-named towns in different countries get their own page.
 */
export function townSlug(entry: PlaceEntry): string {
  return `${normalizePlaceName(entry[0]).replace(/ /g, '-')}-${entry[2].toLowerCase()}`;
}

/** The data file to look a town's address up in, or null if it isn't one. */
export function townFileKey(slug: string): string | null {
  const m = /^([a-z0-9]+(?:-[a-z0-9]+)*)-([a-z]{2})$/.exec(slug);
  return m ? placeFileKey(m[1]!.replace(/-/g, ' ')) : null;
}

/** The town at an address (the most populous, if a country has two of that name), or null. */
export function findTown(entries: readonly PlaceEntry[], slug: string): PlaceEntry | null {
  let best: PlaceEntry | null = null;
  for (const entry of entries) {
    if (townSlug(entry) === slug && (!best || entry[5] > best[5])) best = entry;
  }
  return best;
}
